import { useEffect, useState } from "react";

import {
  buildPath,
  buildTemplatePath,
  Button,
  ListContainer,
  ListContext,
  Page,
  
  PageRoute,
  pn,
  SearchBar,
  Typography,
  Stack,
} from "@compass/core";

import { ListFooter } from "components";
import {
  ApplicationListDto,
  ApplicationListResource,
  ListFilterBase,
  Permission,
  PermissionAction,
} from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { applicationPageRoute } from "../ApplicationPage/ApplicationPage";
import { ApplicationTable } from "./ApplicationTable";

const routeName = "applications";

export const applicationListPageRoute: PageRoute = {
  handle: "Applications",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const ApplicationListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.Form;
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<ListFilterBase>({
    sortObjects: [{ property: pn<ApplicationListDto>("id"), isDesc: true }],
  });
  const [listItems, setListItems] = useState<ApplicationListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<ApplicationListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const { tableContainerRef, loadList, applyPage, applyPageSize, applySortMethod, applySort, applySearch } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.applications.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.applications.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    fetchResource();
    loadList(listFilter);
  }, []);

  const fetchResource = () => {
    ApiService.applications.getListResource().then((response) => {
      setResource(response);
    });
  };

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{applicationListPageRoute.handle}</Typography>

            <Stack direction="row" alignItems="center">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  onClick={() => navigate(applicationPageRoute.getPath({ id: 0, crudMode: "edit" }))}
                  variant="outlined"
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={<SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />}
          tableContainer={
            <ApplicationTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              applications={listItems}
              resource={resource}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider value={{ loadList: () => loadList(listFilter) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
